import { Trans, useLingui } from '@lingui/react/macro';
import { useMemo, useState } from 'react';

import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TooltipProvider } from '@/components/ui/tooltip';
import { DAY_COLUMNS, scheduleToDays, toDisplayDayIndex } from '@/domain/templates/day-of-week';
import { formatHoursDisplay, type PreviewMode } from '@/domain/templates/preview-utils';
import { getShiftColour, SHIFT_PILL_CLASSES } from '@/domain/templates/shift-colours';
import { resolveInstanceCount } from '@/domain/templates/staffing';
import { calculateShiftDuration } from '@/domain/templates/template-utils';
import type { ShiftSet } from '@/types/template';

import { CollapsiblePreviewSection, resolveShiftColorKey } from './collapsible-preview-section';
import { DayLabel } from '@/components/build-workspace/day-label';
import { TemplateStaffGroupView } from './template-staff-group-view';

/**
 * Week-grid preview of a template's shifts (WW5). Renders one row per
 * resolved shift type with the shift's time-range pills laid out across
 * a Mon → Sun column grid. A sticky header shows day labels with
 * per-day shift count + total hours.
 *
 * Features:
 * - **Shift Type / Staff Group toggle** — two view modes matching v1.
 * - **Collapsible rows** — each shift-type row can be collapsed.
 * - **Hover tooltips** — each pill shows staffing method, groups, tags,
 *   and assigned employee on hover.
 * - **Hover scale effect** — pills scale up slightly on hover for
 *   visual feedback.
 */
export function VisualPreview({ shifts }: VisualPreviewProps) {
  const { t } = useLingui();
  const [previewMode, setPreviewMode] = useState<PreviewMode>('shift-type');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const groups = useMemo(() => groupByShiftType(shifts), [shifts]);
  const perDay = useMemo(() => calculatePerDayStats(shifts), [shifts]);

  function toggleSection(name: string) {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }

  return (
    <div className="space-y-3">
      <Tabs value={previewMode} onValueChange={(v) => setPreviewMode(v as PreviewMode)}>
        <TabsList>
          <TabsTrigger value="shift-type">
            <Trans>Shift type</Trans>
          </TabsTrigger>
          <TabsTrigger value="staff-group">
            <Trans>Staff group</Trans>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="overflow-hidden p-0">
          <TooltipProvider delayDuration={300}>
            <HeaderRow perDay={perDay} />

            {previewMode === 'staff-group' ? (
              <TemplateStaffGroupView shifts={shifts} />
            ) : (
              <div className="divide-y">
                {groups.map((group) => (
                  <CollapsiblePreviewSection
                    key={group.name}
                    name={group.name}
                    swatchColor={SHIFT_PILL_CLASSES[getShiftColour(group.name)].swatch}
                    perDay={group.perDay}
                    totalShifts={group.totalShifts}
                    totalHours={group.totalHours}
                    collapsed={collapsedSections.has(group.name)}
                    onToggle={() => toggleSection(group.name)}
                    rowTestId={`visual-preview-row-${group.name}`}
                    cellTestIdPrefix={`visual-preview-cell-${group.name}`}
                  />
                ))}
              </div>
            )}
          </TooltipProvider>
        </CardContent>
      </Card>

      {/* Accessible label for the toggle so screen readers understand context */}
      <p className="sr-only">
        {previewMode === 'shift-type'
          ? t`Viewing shifts grouped by shift type`
          : t`Viewing shifts grouped by staff group`}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header row
// ---------------------------------------------------------------------------

/**
 * Sticky column header. Each cell shows the day label plus a shift
 * count and total hours for that day, aggregated across all
 * staff-instances.
 */
function HeaderRow({ perDay }: { perDay: ReadonlyArray<{ shifts: number; hours: number }> }) {
  return (
    <div className="bg-background sticky top-0 z-10 grid grid-cols-7 border-b">
      {DAY_COLUMNS.map((column, index) => {
        const dayShifts = perDay[index].shifts;
        const dayHours = formatHoursDisplay(perDay[index].hours);
        return (
          <div
            key={column.key}
            data-testid={`visual-preview-column-${column.key}`}
            className="border-r p-3 text-center last:border-r-0"
          >
            <div className="text-[length:var(--body-font-size)] leading-[var(--body-line-height)] font-semibold">
              <DayLabel columnKey={column.key} />
            </div>
            <div className="text-muted-foreground text-[length:var(--small-font-size)] leading-[var(--small-line-height)]">
              {dayShifts === 1 ? <Trans>1 shift</Trans> : <Trans>{dayShifts} shifts</Trans>}
            </div>
            <div className="text-primary text-[length:var(--small-font-size)] leading-[var(--small-line-height)] font-medium">
              <Trans>{dayHours} hours</Trans>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data helpers
// ---------------------------------------------------------------------------

/**
 * Group every shift by its resolved shift-type name, populate each
 * group's 7-day pill matrix, and roll up per-group totals.
 */
function groupByShiftType(shifts: ShiftSet[]): ShiftTypeGroup[] {
  const map = new Map<string, ShiftTypeGroup>();

  for (const shift of shifts) {
    const name = resolveGroupName(shift);
    const group =
      map.get(name) ??
      ({
        name,
        perDay: Array.from({ length: 7 }, () => [] as ShiftSet[]),
        totalShifts: 0,
        totalHours: 0,
      } satisfies ShiftTypeGroup);

    const instances = resolveInstanceCount(shift);
    const hoursPerInstance = calculateShiftDuration(shift.startTime, shift.endTime);

    for (const dayOfWeek of scheduleToDays(shift.schedule)) {
      const displayIndex = toDisplayDayIndex(dayOfWeek);
      for (let i = 0; i < instances; i++) {
        group.perDay[displayIndex].push(shift);
      }
      group.totalShifts += instances;
      group.totalHours += hoursPerInstance * instances;
    }

    map.set(name, group);
  }

  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Per-column day totals — shift count and total hours.
 */
function calculatePerDayStats(
  shifts: ShiftSet[],
): ReadonlyArray<{ shifts: number; hours: number }> {
  const totals = Array.from({ length: 7 }, () => ({ shifts: 0, hours: 0 }));
  for (const shift of shifts) {
    const instances = resolveInstanceCount(shift);
    const hoursPerInstance = calculateShiftDuration(shift.startTime, shift.endTime);
    for (const dayOfWeek of scheduleToDays(shift.schedule)) {
      const displayIndex = toDisplayDayIndex(dayOfWeek);
      totals[displayIndex].shifts += instances;
      totals[displayIndex].hours += hoursPerInstance * instances;
    }
  }
  return totals;
}

/** Derives the display name for a shift's group row. */
function resolveGroupName(shift: ShiftSet): string {
  return resolveShiftColorKey(shift);
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VisualPreviewProps {
  shifts: ShiftSet[];
}

interface ShiftTypeGroup {
  name: string;
  perDay: ShiftSet[][];
  totalShifts: number;
  totalHours: number;
}
